import type { UseMutationOptions } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest, type ApiError, useApiMutation } from "@/shared/api";

export const COMPLIANCE_COMPLETED_STORAGE_KEY = "nommia.compliance.completed";

export type ComplianceRequest = {
  isPep: boolean;
  isFatca: boolean;
};

export type ComplianceResponse = {
  message: string;
};

export const hasCompletedCompliance = () => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return (
      window.localStorage.getItem(COMPLIANCE_COMPLETED_STORAGE_KEY) === "true"
    );
  } catch {
    return false;
  }
};

export const setCompletedCompliance = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(COMPLIANCE_COMPLETED_STORAGE_KEY, "true");
  } catch {
    // Ignore storage errors so compliance submission can still succeed.
  }
};

const updateComplianceRequest = (payload: ComplianceRequest) => ({
  url: "/profile/compliance",
  method: "post" as const,
  data: payload,
});

export const updateCompliance = (payload: ComplianceRequest) =>
  apiRequest<ComplianceResponse>(updateComplianceRequest(payload));

export const useUpdateCompliance = (
  options?: UseMutationOptions<ComplianceResponse, ApiError, ComplianceRequest>
) => {
  const queryClient = useQueryClient();

  return useApiMutation<ComplianceResponse, ComplianceRequest>(
    updateComplianceRequest,
    {
      ...options,
      onSuccess: (data, variables, context, mutation) => {
        queryClient.invalidateQueries({ queryKey: ["profile"] });
        options?.onSuccess?.(data, variables, context, mutation);
      },
    }
  );
};
